<template>
  <b-row class="px-2 mt-3">
    <b-col>
      <div class="iq-card">
        <div class="iq-card-header d-flex justify-content-between">
          <div class="iq-header-title">
            <h5 class="card-title">{{ chartTitle }}</h5>
          </div>
        </div>
        <div class="card-info-container" :class="{ 'card-info-container--center-avg': infoSections.length === 1 }">
          <div class="card-info-section" v-for="section in infoSections" :key="section.key">
            <h5 class="">{{ section.value }}</h5>
            <span class="">{{ section.label }}</span>
          </div>
        </div>

        <div class="rpm-fuel-speed-chart">
          <rpm-fuel-speed
            :series="currAssetSummary.series"
            :deviceStatus="deviceStatus"
          />
        </div>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { prettyLabel } from '@/util/ship/vesselDetail';
import { getRpmTypeLabel } from '../../util/ship/fms-summary';
import RpmFuelSpeed from '../charts/RpmFuelSpeed.vue';

export default {
  name: 'RpmFuelSpeedCard',
  components: {
    RpmFuelSpeed,
  },
  props: {
    currAssetSummary: {
      type: Object,
      required: true,
    },
    deviceStatus: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    hasRpm() {
      return (this.deviceStatus?.rpmTypes || []).length > 0;
    },
    hasFuel() {
      return (this.deviceStatus?.flowmeterGroups || []).length > 0;
    },
    chartTitle() {
      // Judul ngikut data yang benar-benar diplot (RPM hilang buat asset tanpa RPM).
      const parts = [];
      if (this.hasRpm) parts.push('RPM');
      if (this.hasFuel) parts.push('Fuel Consumption');
      parts.push('Speed');
      if (parts.length >= 3) {
        return `${parts.slice(0, -1).join(', ')}, And ${parts[parts.length - 1]} Chart`;
      }
      if (parts.length === 2) return `${parts[0]} And ${parts[1]} Chart`;
      return `${parts[0]} Chart`;
    },
    infoSections() {
      const sections = [];
      const { fm, rpm, gps } = this.currAssetSummary || {};
      const rpmTypes = this.deviceStatus?.rpmTypes || [];
      const groups = this.deviceStatus?.flowmeterGroups || [];
      if (!fm || !gps) return [];

      const rpmPositions = new Set(rpmTypes.map((t) => String(t).toUpperCase()));

      // Pasangan RPM + fuel per main engine (PORT/STBD/CENTER).
      ['PORT', 'STARBOARD', 'CENTER'].forEach((pos) => {
        if (!rpm || !rpm[pos]) return;
        const displayLabel = getRpmTypeLabel(pos, rpmTypes);
        sections.push({ key: `${pos}-rpm`, label: `Avg. ${displayLabel} RPM`, value: rpm[pos].average });
        if (fm[pos] && fm[pos].in) {
          sections.push({ key: `${pos}-fm`, label: `${displayLabel} Fuel Cons`, value: `${fm[pos].in.engineCons} Liter` });
        }
      });

      // Fuel cons untuk line flowmeter non-main-engine (mis. AE-line di AWB).
      groups.forEach((g) => {
        if (rpmPositions.has(String(g.name).toUpperCase())) return;
        const data = fm[g.name];
        if (!data) return;
        const cons = data.in ? data.in.engineCons : (data.out && data.out.engineCons);
        if (cons === undefined || cons === null) return;
        sections.push({ key: `${g.name}-fm`, label: `${prettyLabel(g.name)} Fuel Cons`, value: `${cons} Liter` });
      });

      sections.push({ key: 'avg-speed', label: 'Avg. Speed', value: `${gps.averageSpeed} Knot` });

      return sections;
    },
  },
};
</script>

<style scoped>
.card-info-container {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  padding: 10px 15px;
}

.card-info-container--single {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.card-info-container--center-avg {
  grid-template-columns: 1fr;
  justify-items: center;
}

.card-info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

@media screen and (max-width: 991px) {
  .card-info-container {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-rows: auto auto;
  }
}

.rpm-fuel-speed-chart {
  margin: 0 1rem 1rem;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

@media screen and (max-width: 575px) {
  .iq-card-header {
    padding-left: 16px;
    padding-right: 16px;
  }

  .card-title {
    font-size: 1rem;
    line-height: 1.35;
  }

  .card-info-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 8px;
    padding: 8px 12px 12px;
  }

  .card-info-container--center-avg {
    grid-template-columns: 1fr;
  }

  .card-info-section h5 {
    font-size: 1rem;
    margin-bottom: 2px;
  }

  .card-info-section span {
    font-size: 0.75rem;
    line-height: 1.2;
  }

  .rpm-fuel-speed-chart {
    margin: 0 0.75rem 0.75rem;
    padding-bottom: 6px;
  }
}
</style>
